import { EMPTY, Observable, of } from 'rxjs';
import { SlackUserDisplayName, SlackUserId, SlackUserImage, SlackUserName } from '../../value';
import { distinct, expand, filter, map, mergeMap, take } from 'rxjs/operators';

import { FirestoreQueryBuilder } from '../../../../../lib/gcp/builder/firestore-query.builder';
import { IFirestoreService } from '../../../../../lib/gcp/service/firestore.service';
import { ISlackUserRepository } from '../../repository';
import { IsDeleted } from '../../../../kernel/base/value';
import { SlackTeamId } from '../../../slack-team/value';
import { SlackUser } from '../../entity';
import { SlackUserNotFoundError } from '../../exception';
import { Timestamp } from '../../../../../utility/model/timestamp.value';

export class SlackUserFirestoreRepository implements ISlackUserRepository {
  private static readonly collectionId = 'slack_user';

  constructor(private readonly firestoreService: IFirestoreService) { }

  select(id: SlackUserId): Observable<SlackUser> {
    return this.firestoreService.getDocument(SlackUserFirestoreRepository.collectionId, id).pipe(
      map(item => {
        if (!item || item && item.isDeleted === true) {
          throw new SlackUserNotFoundError(`slack_user id「${id.value}」 is not found`);
        }
        return this.convertToEntity(item);
      })
    );
  }

  selectIfExists(id: SlackUserId): Observable<SlackUser> | null {
    return this.firestoreService.getDocument(SlackUserFirestoreRepository.collectionId, id).pipe(
      map(item => {
        if (!item || item && item.isDeleted === true) {
          return null;
        }
        return this.convertToEntity(item);
      })
    );
  }

  selectAll(builder: FirestoreQueryBuilder<SlackUser>): Observable<SlackUser> {
    return this.firestoreService.getCollection(SlackUserFirestoreRepository.collectionId, builder).pipe(
      take(1),
      expand(items =>
        items.length
          ? this.firestoreService
            .getCollection(SlackUserFirestoreRepository.collectionId, builder.startAfter(items[items.length - 1].id).limit(100))
            .pipe(take(1))
          : EMPTY
      ),
      mergeMap(items => items),
      distinct(item => item.id),
      filter(item => item.isDeleted !== true),
      map(item => this.convertToEntity(item))
    );
  }

  insert(item: SlackUser): Observable<SlackUser> {
    item.createdAt = Timestamp.createByDate(new Date());
    item.updatedAt = Timestamp.createByDate(new Date());
    item.isDeleted = IsDeleted.create(false);
    return this.firestoreService.setDocument(SlackUserFirestoreRepository.collectionId, this.convertToMap(item)).pipe(map(() => item));
  }

  update(user: SlackUser): Observable<SlackUser> {
    user.updatedAt = Timestamp.createByMillsec(+new Date());
    return this.firestoreService.getDocument(SlackUserFirestoreRepository.collectionId, user.id).pipe(
      take(1),
      mergeMap(item => {
        if (!item || item && item.isDeleted === true) {
          throw new SlackUserNotFoundError(`slack_user id「${user.id.value}」 is not found`);
        }
        return this.firestoreService.setDocument(SlackUserFirestoreRepository.collectionId, this.convertToMap(user));
      }),
      map(_ => user)
    );
  }

  delete(id: SlackUserId): Observable<void> {
    const proposal = new SlackUser(id);
    proposal.isDeleted = IsDeleted.create(true);
    proposal.updatedAt = Timestamp.createByMillsec(+Date.now());
    return this.firestoreService.setDocument(SlackUserFirestoreRepository.collectionId, this.convertToMap(proposal));
  }

  generateId(): SlackUserId {
    return SlackUserId.create(this.firestoreService.generateId());
  }

  private convertToMap(user: SlackUser): object {
    return SlackUser.allFields.reduce((p, key) => {
      if (key === 'name') {
        p[key] = Object.keys(user.name.value).reduce((pp, c) => {
          pp[c] = user.name.value[c] || '';
          return pp;
        }, {});
        return p;
      }
      if (user[key] === undefined) {
        return p;
      }
      const value = user[key] as { value: any };
      p[key] = value.value;
      return p;
    }, {});
  }

  private convertToEntity(item: any) {
    const slackUser = new SlackUser(SlackUserId.create(item.id));
    slackUser.teamId = SlackTeamId.create(item.teamId);
    slackUser.name = SlackUserName.create(item.name);
    slackUser.displayName = SlackUserDisplayName.create(item.displayName);
    slackUser.image = SlackUserImage.create(item.image);
    slackUser.createdAt = Timestamp.createByMillsec(item.createdAt.seconds * 1000);
    slackUser.updatedAt = Timestamp.createByMillsec(item.updatedAt.seconds * 1000);
    slackUser.isDeleted = item && item.isDeleted ? IsDeleted.create(item.isDeleted) : IsDeleted.create(false);
    return slackUser;
  }
}
